// React Refresh handles hot reloading automatically
import Pleasure from './theme/pleasure.js';
require('./styles/app.less');

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Router, Route, Switch, Redirect } from 'react-router';
import $ from 'jquery';
import _ from 'lodash';
import Favicon from 'react-favicon';
import UserManager from './core/UserManager';
import { useSelector } from 'react-redux';
import { store } from './store/store';
import history from './history';
import RouterHelper from './core/RouterHelper';
import PubSub from 'pubsub-js';
import GlobalConst from 'utils/GlobalConst';
import Button from 'components/markup/Button';
import Error from './components/Error';
import Menu from './components/base/iq_series_base_ui/Menu.js';
import AnalyticsManager from './core/AnalyticsManager';
import { Modal, ModalFooter, ModalHeader } from './components/markup/Modal';
import ContactUsModal from './components/auth/ContactUsModal';
import ReactTooltip from 'react-tooltip';
import GDPRCookiesBox from './components/auth/GDPRCookiesBox.js';
import AuthDataProvider from './components/auth/AuthDataProvider';
import NumberInputModal from './components/auth/NumberInputModal';
import WhiteLabelController from './core/WhiteLabelController';
import ExportsManagerDataProvider from './components/exports_manager/ExportsManagerDataProvider';
import NotificationsDataProvider from './components/notifications_manager/NotificationsManagerDataProvider.js';
import AppLoadingPage from './components/common/AppLoadingPage';
import AtlasHelper from './components/atlas/AtlasHelper';

const componentsCache = {};

class AsyncComponent extends PureComponent {

	static propTypes = {
		getComponent: PropTypes.func
	};

	constructor(props) {
		super(props);
		this.state = {Component: componentsCache[props.match.path]};
	}

	componentDidMount() {
		this.resolveComponent(this.props);
	}

	componentDidUpdate(prevProps){
		if (prevProps.match.path !== this.props.match.path) {
			this.setState({Component: null});
			this.resolveComponent(this.props);
		}
	}

	resolveComponent(props) {
		const path = props.match.path;
		const cached = componentsCache[path];
		if(!cached) {
			props.getComponent().then(module => {
				const component = module.default || module;
				componentsCache[path] = component;
				this.setState({Component: component});
			})
		} else {
			this.setState({Component: cached});
		}
	}

	render() {
		const { Component } = this.state;
		if (Component) {
			return <Component {...this.props}/>;
		}
		return null;
	}

}

export const AutoRedirectRoute = ({ component: Component, getComponent, checkRedirect, extraProps, ...rest }) => (
	<Route {...rest} render={props => {
		if(checkRedirect) {
			const redirectTo = checkRedirect();
			if(!_.isEmpty(redirectTo)) {
				return <Redirect to={redirectTo}/>;
			}
		}
		const resultProps = _.isEmpty(extraProps) ? props : {...props, ...extraProps};
		const key = props.match.path;
		return Component ? <Component key={key} {...resultProps}/> : <AsyncComponent key={key} {...resultProps} getComponent={getComponent}/>;
	}}/>
);

// Authenticated side-menu routes rendered inside renderSideMenuRoutes(). Each
// row resolves `component` against `componentMap` (built per-render so it can
// pick the right sync/async loader) and `flag` against the current auth user
// via checkPermission(). Use `extraPropsPath` only when propsToComponents()
// needs a non-default argument (e.g. role-permissions screens).
const SIDE_MENU_ROUTES = [
	{ path: '/home',                                            component: 'Dashboard' },
	{ path: '/support_dashboard',                               component: 'SupportDashboard',     flag: 'canAccessSupportDashboard' },

	{ path: '/streamliner',                                     component: 'Streamliner',          flag: 'canAccessStreamliner' },
	{ path: '/streamliner/:platform/setup/:id',                 component: 'StreamlinerSetup',     flag: 'canAccessStreamliner' },

	{ path: '/activation_guard',                                component: 'QAAutomation',         flag: 'canAccessQAAutomation' },
	{ path: '/activation_guard/:id',                            component: 'QAAutomationDetails',  flag: 'canAccessQAAutomation' },

	{ path: '/forecasting',                                     component: 'Forecasting',          flag: 'canAccessForecasting' },

	{ path: '/inspect',                                         component: 'Inspect',              flag: 'canAccessInspect' },
	{ path: '/inspect/meta/:inspectID',                         component: 'InspectDetails',       flag: 'canAccessInspect' },
	{ path: '/inspect/youtube/:inspectID',                      component: 'InspectDetails',       flag: 'canAccessInspect' },

	{ path: '/pacing_iq',                                       component: 'PacingIQ',             flag: 'canAccessPacingIQ' },

	{ path: '/auto_bidder',                                     component: 'AdBidder',             flag: 'canAccessAdBidder' },
	{ path: '/auto_bidder/:id',                                 component: 'AdBidderDetails',      flag: 'canAccessAdBidder' },

	{ path: '/recipes',                                         component: 'RecipesList' },
	{ path: '/recipes/new',                                     component: 'RecipesBuilder',       flag: 'canAccessRecipes' },
	{ path: '/recipes/edit/:id',                                component: 'RecipesBuilder',       flag: 'canAccessRecipes' },

	{ path: '/extension_guide',                                 component: 'ExtensionGuide',       flag: 'canAccessIQExtension' },

	{ path: '/users',                                           component: 'UsersList',            flag: 'canAccessUserManager' },
	{ path: '/users/permissions_log',                           component: 'UserPermissionsLog',   flag: 'canAccessUserManager' },
	{ path: '/users/permissions/:userID',                       component: 'AdminUserPermissions', flag: 'canAccessUserManager' },
	{ path: '/roles/permissions/:userID',                       component: 'AdminUserPermissions', flag: 'canAccessUserManager', extraPropsPath: '/roles/permissions/' },
	{ path: '/roles/permissions/',                              component: 'AdminUserPermissions', flag: 'canAccessUserManager', extraPropsPath: '/roles/permissions/' },
	{ path: '/domain_manager',                                  component: 'DomainManager',        flag: 'canAccessDomainManager' },
	{ path: '/data_tracker',                                    component: 'DataTracker',          flag: 'canAccessDataTracker' },
	{ path: '/notifications_manager',                           component: 'NotificationsManager', flag: 'canAccessNotificationsManagerCreate' },

	{ path: '/my_profile',                                      component: 'UserSettings' },
	{ path: '/exports_manager',                                 component: 'ExportsManager' },
	{ path: '/personas_manager',                                component: 'PersonasManager',      flag: 'canAccessPersonas' },
	{ path: '/data_atlas',                                      component: 'Atlas',                flag: 'canAccessDataExplorer' }
];

const SIDE_MENU_REDIRECTS = [
	{ from: '/dashboard',                   to: '/home' },
	{ from: '/admin/support_dashboard',     to: '/support_dashboard' },
	{ from: '/pacing_iq',                   to: '/pacing_iq' },
	{ from: '/auto_bidder',                 to: '/auto_bidder' },
	{ from: '/tools/extension_guide',       to: '/extension_guide' },
	{ from: '/admin/users',                 to: '/users' },
	{ from: '/admin/domain_manager',        to: '/domain_manager' },
	{ from: '/admin/data_tracker',          to: '/data_tracker' },
	{ from: '/admin/notifications_manager', to: '/notifications_manager' },
	{ from: '/admin',                       to: '/users' },
	{ from: '/qa_automation/:id',           to: '/activation_guard/:id' },
	{ from: '/qa_automation',               to: '/activation_guard' }
];

// Async loaders for production code-splitting. Each value is a thunk so
// webpack's static analysis can keep emitting one chunk per import().
const ASYNC_LOADERS = {
	Login:                () => import('./components/auth/Login'),
	PasswordRestore:      () => import('./components/auth/PasswordRestore'),
	ReferralTokenPage:    () => import('./components/base/iq_series_base_ui/referral_token_page/ReferralTokenPage.js'),
	Inspect:              () => import('./components/inspect/Inspect'),
	InspectDetails:       () => import('./components/inspect/InspectDetails.js'),
	UserSettings:         () => import('./components/auth/UserSettings'),
	UsersList:            () => import('./components/admin/UsersList'),
	UserPermissionsLog:   () => import('./components/admin/UserPermissionsLog'),
	NotificationsManager: () => import('./components/notifications_manager/NotificationsManager'),
	DomainManager:        () => import('./components/domain_manager/DomainManager'),
	AdminUserPermissions: () => import('./components/admin/AdminUserPermissions'),
	DataTracker:          () => import('./components/data_tracker/DataTracker'),
	AdBidder:             () => import('./components/ad_bidder/AdBidder.js'),
	AdBidderDetails:      () => import('./components/ad_bidder/AdBidderDetailsPage.js'),
	RecipesList:          () => import('./components/recipes/RecipesList.js'),
	RecipesBuilder:       () => import('./components/recipes/builder/RecipesBuilder.js'),
	PacingIQ:             () => import('./components/pacing_iq/PacingIQ.js'),
	Forecasting:          () => import('./components/forecasting/Forecasting'),
	ExtensionGuide:       () => import('./components/extension_guide/ExtensionGuide'),
	AccessDeniedPage:     () => import('./components/AccessDeniedPage'),
	NotFound:             () => import('./components/NotFound'),
	WelcomePage:          () => import('./components/WelcomePage'),
	ErrorPage:            () => import('./components/ErrorPage'),
	ExportsManager:       () => import('./components/exports_manager/ExportsManager'),
	Dashboard:            () => import('./components/dashboard/Dashboard.js'),
	SupportDashboard:     () => import('./components/support_dashboard/SupportDashboard.js'),
	Streamliner:          () => import('./components/streamliner/Streamliner.js'),
	StreamlinerSetup:     () => import('./components/streamliner/StreamlinerSetup.js'),
	QAAutomation:         () => import('./components/qa_automation/QAAutomation.js'),
	QAAutomationDetails:  () => import('./components/qa_automation/QAAutomationDetails.js'),
	PersonasManager:      () => import('./components/personas/PersonasManager.js'),
	Atlas:                () => import('./components/atlas/Atlas.js')
};

// Eagerly-resolved components for development (code-splitting is disabled there
// because it doesn't HMR cleanly). The process.env.NODE_ENV guard is required
// so webpack's DefinePlugin + dead-code elimination strip every require() from
// the production bundle — otherwise every chunk would be inlined into main.
const SYNC_COMPONENTS = process.env.NODE_ENV === 'development'
	? {
		Login:                require('./components/auth/Login').default,
		PasswordRestore:      require('./components/auth/PasswordRestore').default,
		ReferralTokenPage:    require('./components/base/iq_series_base_ui/referral_token_page/ReferralTokenPage.js').default,
		Inspect:              require('./components/inspect/Inspect.js').default,
		InspectDetails:       require('./components/inspect/InspectDetails.js').default,
		UserSettings:         require('./components/auth/UserSettings').default,
		UsersList:            require('./components/admin/UsersList').default,
		UserPermissionsLog:   require('./components/admin/UserPermissionsLog').default,
		NotificationsManager: require('./components/notifications_manager/NotificationsManager').default,
		DomainManager:        require('./components/domain_manager/DomainManager').default,
		AdminUserPermissions: require('./components/admin/AdminUserPermissions').default,
		DataTracker:          require('./components/data_tracker/DataTracker').default,
		AdBidder:             require('./components/ad_bidder/AdBidder.js').default,
		AdBidderDetails:      require('./components/ad_bidder/AdBidderDetailsPage.js').default,
		RecipesList:          require('./components/recipes/RecipesList.js').default,
		RecipesBuilder:       require('./components/recipes/builder/RecipesBuilder.js').default,
		PacingIQ:             require('./components/pacing_iq/PacingIQ.js').default,
		Forecasting:          require('./components/forecasting/Forecasting').default,
		ExtensionGuide:       require('./components/extension_guide/ExtensionGuide').default,
		AccessDeniedPage:     require('./components/AccessDeniedPage').default,
		NotFound:             require('./components/NotFound').default,
		WelcomePage:          require('./components/WelcomePage').default,
		ErrorPage:            require('./components/ErrorPage').default,
		ExportsManager:       require('./components/exports_manager/ExportsManager').default,
		Dashboard:            require('./components/dashboard/Dashboard.js').default,
		SupportDashboard:     require('./components/support_dashboard/SupportDashboard.js').default,
		Streamliner:          require('./components/streamliner/Streamliner.js').default,
		StreamlinerSetup:     require('./components/streamliner/StreamlinerSetup.js').default,
		QAAutomation:         require('./components/qa_automation/QAAutomation.js').default,
		QAAutomationDetails:  require('./components/qa_automation/QAAutomationDetails.js').default,
		PersonasManager:      require('./components/personas/PersonasManager.js').default,
		Atlas:                require('./components/atlas/Atlas.js').default
	}
	: null;

class Routes extends PureComponent {

	constructor(props) {
		super(props);
		this.state = {
			modalMessage: null,
			didAutoLogin: false,
			showGDPRBox: true,
			siteAssets: null,
			loadingUserData: true,
			googleAdsUserData: {},
			dv360UserData: {},
			metaUserData: {},
			oauthRequired: true,
			showContactUs: false,
			exportsStatus: {},
			// Notifications
			initialNotifications: false,
			notifications: [],
			loadingNotifications: false,
			sideDown: false
		};
		$(document).on('click', '.dropdown-menu.noclose > li', e => e.stopPropagation());
		$(document).on('click', '.dropdown-menu > li > .noclose', e => e.stopPropagation());
	}

	getAuthUser = () => this.props.auth?.user || {};

	componentDidMount() {
		Pleasure.init();
		WhiteLabelController.getLocalAssets().then(data => this.setState({ siteAssets: data }));
		AnalyticsManager.sendPageViewAnalytics();
		this.routesUserLogin();
		// Pubsub subscriptions
		PubSub.subscribe(GlobalConst.SHOW_MODAL_MSG, (type, modalMessage) => {
			if(modalMessage && modalMessage.type === GlobalConst.MODAL_TYPE_CONTACT_US) {
				this.setState({ showContactUs: true }, () => $('#app-contact-us-modal').modal('show'));
				return;
			}
			this.setState({ modalMessage }, () => {
				$(`#app-modal-${modalMessage.type}`).modal('show');
			})
		});
		PubSub.subscribe('FETCH_OAUTH_DATA', () => this.fetchUserData());
		PubSub.subscribe('CLEAR_OAUTH', () => this.clearOAuthData());
		PubSub.subscribe('CLEAR_GADS_OAUTH', () => this.clearSingleOAuth('googleAdsUserData'));
		PubSub.subscribe('CLEAR_DV360_OAUTH', () => this.clearSingleOAuth('dv360UserData'));
		PubSub.subscribe('CLEAR_META_OAUTH', () => this.clearSingleOAuth('metaUserData'));
		PubSub.subscribe('TRIGGER_NOTIFICATIONS_INITIAL', () => {
			if (!this.state.initialNotifications) {
				this.setState({ initialNotifications: true });
				this.onFetchExportsStatus();
				this.exportsInterval = setInterval(() => this.onFetchExportsStatus(), 120000);
				if (this.getAuthUser().canAccessSeeNotifications) {
					this.fetchUserNotifications(false);
					this.notificationsInterval = setInterval(() => this.fetchUserNotifications(true), 120000);
				}
			}
		});
		PubSub.subscribe('CLEAR_TRIGGER_NOTIFICATIONS', () => {
			this.setState({ initialNotifications: false });
			clearInterval(this.exportsInterval);
			clearInterval(this.notificationsInterval);
		});
	}

	routesUserLogin = () => {
		let magicToken = false;
		let currentQuery = RouterHelper.currentQuery();
		if (currentQuery.emailLogin) {
			magicToken = currentQuery.magicLink;
		} else if (currentQuery.referral_token) {
			UserManager.clearAuthData();
		}
		UserManager.autoLogin(magicToken)
			.catch(xhrError => {
				if (xhrError?.responseStatus === 400) {
					PubSub.publish(GlobalConst.SHOW_MODAL_MSG, {type: 3, message: xhrError.json.message});
				}
			})
			.finally(() => {
			RouterHelper.setTitle();
			this.setState({didAutoLogin: true}, () => {
				const authState = store.getState().auth || {};
				const authUser = authState.user || {};
				const isAuthorized = authState.isAuthorized;
				if (location.pathname === '/referral_token') return;
				if ((location.pathname === '/' || location.pathname === '/login') && isAuthorized) return RouterHelper.goToDefaultRoute();
				else if (location.pathname !== '/' && isAuthorized && !authUser.isActive && authUser.email) return RouterHelper.goToWelcomePage();
				else if (location.pathname !== '/' && location.pathname !== '/password_reset/' && location.pathname !== '/referral_token' && location.pathname !== '/login' && !isAuthorized) return RouterHelper.goToLogin();
				else if (location.pathname !== '/' && location.pathname !== '/password_reset/' && location.pathname !== '/referral_token' && location.pathname !== '/login'&& !authUser.email) return RouterHelper.goToErrorPage();
			});
		});
	}

	clearSingleOAuth = userDataKey => {
		this.setState({ [userDataKey]: {} });
	}

	fetchUserData = () => {
        this.setState({ loadingUserData: true });
        return AuthDataProvider.fetchOAuthUserData()
            .then(data => {
                this.setState({ googleAdsUserData: data.gads, dv360UserData: data.dv360, metaUserData: data.meta, oauthRequired: !data.bypass_oauth });
                RouterHelper.removeFromQuery('oauth_code');
            })
            .catch(() => PubSub.publish(GlobalConst.SHOW_MODAL_MSG, {type: 3, message: {readableMessage: 'Error getting user oauth data'}}))
            .finally(() => this.setState({ loadingUserData: false }));
    }

	clearOAuthData = () => {
		this.setState({ googleAdsUserData: {}, dv360UserData: {}, metaUserData: {}, loadingUserData: false });
	}

	onFetchExportsStatus = () => {
		ExportsManagerDataProvider.getExportsNotifications()
			.then(status => this.setState({ exportsStatus: status }));
	}

	// Notifications
	fetchUserNotifications = (autoRefresh=false) => {
		if (autoRefresh) this.setState({ loadingNotifications: true });
		NotificationsDataProvider.getUserNotifications()
			.then(data => this.setState({ notifications: data, loadingNotifications: false }));
	}

	// Factory used by every permission-gated route. Pass the auth-user flag
	// name and receive a checkRedirect callback compatible with AutoRedirectRoute.
	checkPermission = (flag) => () => {
		if (!this.getAuthUser()[flag]) {
			return RouterHelper.accessDeniedPageURL();
		}
	}

	hideModal(callback){
		this.setState({modalMessage: null}, callback);
	}

	onGDPRCookiesAction = response => {
		AuthDataProvider.onGDPRCookiesAction(response);
		this.setState({ showGDPRBox: false });
	}

	renderModal() {
		const { modalMessage } = this.state;
		if(!modalMessage) {
			return null;
		}
		const { type, message, title, buttons, extraProps={} } = modalMessage;
		let modalTitle, modalLabel;
		let payload = message;
		switch (type) {
			case GlobalConst.MODAL_TYPE_SUCCESS:
				modalLabel = extraProps.label || '';
				modalTitle = title || 'Success';
				break;
			case GlobalConst.MODAL_TYPE_WARN:
				modalLabel = extraProps.label || '';
				modalTitle = title || 'Warning';
				break;
			case GlobalConst.MODAL_TYPE_ERROR:
				modalTitle = extraProps.label ? title || 'Error' : title || '';
				modalLabel = extraProps.label || 'Error';
				payload = <Error error={message}/>;
				break;
			default:
				modalLabel = extraProps.label || '';
				modalTitle = title || 'Info';
				break;
		}
		return (
			<Modal id={`app-modal-${type}`} className='common-app-modal' {...extraProps}>
				{(!_.isEmpty(modalTitle) || !_.isEmpty(modalLabel)) && <ModalHeader>
					{!_.isEmpty(modalLabel) && <span className='modal-label'>{modalLabel}</span>}
					{modalTitle}
					<span className='material-icons close-common-modal' data-dismiss='modal'>close</span>
				</ModalHeader>}
				{payload}
				<ModalFooter>
					{_.isEmpty(buttons) ?
						<Button className="btn-flat btn-default" data-dismiss="modal" onClick={() => this.hideModal()}>Ok</Button> :
						_.map(buttons, button =>
							<Button key={button.title} className={button.className || 'btn-flat btn-default'} data-dismiss="modal" onClick={() => this.hideModal(button.callback)}>
								{button.title}
							</Button>
						)
					}
				</ModalFooter>
			</Modal>
		);
	}

	propsToComponents = (path='') => {
		const { googleAdsUserData, dv360UserData, metaUserData, loadingUserData, oauthRequired, loadingNotifications, notifications } = this.state;
		const globalOAuthData = { googleAdsUserData, dv360UserData, metaUserData, loadingUserData, oauthRequired, fetchUserData: this.fetchUserData };
		if (_.includes(path, 'admin/roles/permissions') || _.includes(path, 'roles/permissions')) return ({ role: true, globalOAuthData, loadingNotifications, notifications });
		return ({ globalOAuthData, loadingNotifications, notifications });
	}

	render() {
		const { auth } = this.props;
		const authUser = auth?.user || {};
		const isAuthorized = auth?.isAuthorized;
		const { didAutoLogin, showGDPRBox, showContactUs, exportsStatus } = this.state;
		const favicon = WhiteLabelController.siteAssets.favicon || require('../src/favicon.png');

		if(!didAutoLogin) {
			return <AppLoadingPage />;
		}

		// Resolve every component against the dev (eager) or prod (lazy) loader
		// map, then destructure the entries that JSX references by name. The map
		// is also consumed positionally by the data-driven SIDE_MENU_ROUTES below.
		const componentMap = SYNC_COMPONENTS
			? _.mapValues(SYNC_COMPONENTS, (component) => ({ component }))
			: _.mapValues(ASYNC_LOADERS, (getComponent) => ({ getComponent }));
		const { Login, PasswordRestore, ReferralTokenPage, WelcomePage, ErrorPage,
			AccessDeniedPage, NotFound } = componentMap;

		const renderSideMenuRoutes = () => {
			return <Menu exportsStatus={exportsStatus} onFetchExportsStatus={this.onFetchExportsStatus}>
				<Switch>
					{_.map(SIDE_MENU_ROUTES, ({ path, component, flag, extraPropsPath }) => (
						<AutoRedirectRoute
							key={path}
							exact
							path={path}
							extraProps={this.propsToComponents(extraPropsPath)}
							checkRedirect={flag ? this.checkPermission(flag) : undefined}
							{...componentMap[component]}
						/>
					))}
					{_.map(SIDE_MENU_REDIRECTS, ({ from, to }) => (
						<Redirect key={`${from}->${to}`} from={from} to={to} />
					))}

					<AutoRedirectRoute {...NotFound}/>
					<AutoRedirectRoute exact path="/access_denied" {...AccessDeniedPage}/>

				</Switch>
				{authUser?.email && !document.cookie.includes(`${authUser.email}=phone_popup`) && !authUser.phoneNumberVerified && <NumberInputModal showOnLoad={true}/>}
				{(authUser?.hasAcceptedGDPR === null || authUser?.hasAcceptedGDPR === undefined) && showGDPRBox ? <GDPRCookiesBox onGDPRCookiesAction={this.onGDPRCookiesAction} /> : null}
				{isAuthorized && authUser.isActive ? <AtlasHelper /> : null}
			</Menu>;
		};

		return (
			<Router history={history}>
				<div>
					<Favicon url={favicon} />
					<Switch>
						<AutoRedirectRoute exact path="/" {...Login}/>
						<AutoRedirectRoute exact path="/login" {...Login}/>
						<AutoRedirectRoute exact path="/password_reset" {...PasswordRestore}/>
						<AutoRedirectRoute exact path="/referral_token" {...ReferralTokenPage}/>
						<AutoRedirectRoute exact path="/welcome" {...WelcomePage}/>
						<AutoRedirectRoute exact path="/maintenance" {...ErrorPage}/>
						<Route render={() => {
							if(isAuthorized && authUser.isActive) {
								return renderSideMenuRoutes();
							}
							return <Redirect to="/login"/>
						}}/>
					</Switch>
					{this.renderModal()}
					{showContactUs && authUser.isActive && <ContactUsModal id="app-contact-us-modal"/>}
					<ReactTooltip id="default-tooltip"/>
					<ReactTooltip id="html-tooltip" html={true}/>
				</div>
			</Router>
		);
	}

}

export const RoutesClass = Routes;

const RoutesContainer = (props) => {
	const auth = useSelector(state => state.auth);

	return <Routes {...props} auth={auth} />;
};

export default RoutesContainer;
